import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import dailyExpenseService from "./dailyExpenseService";

const initialState = {
  dailyExpenses: [],
  dailyExpense: null,

  isCreateLoading: false,
  isCreateError: false,
  isCreateSuccess: false,
  createErrorMessage: "",

  isListLoading: false,
  isListError: false,
  isListSuccess: false,
  listErrorMessage: "",

  isDetailsLoading: false,
  isDetailsError: false,
  isDetailsSuccess: false,
  detailsErrorMessage: "",

  isUpdateLoading: false,
  isUpdateError: false,
  isUpdateSuccess: false,
  updateErrorMessage: "",

  isDeleteLoading: false,
  isDeleteError: false,
  isDeleteSuccess: false,
  deleteErrorMessage: "",
};

//Create DailyExpense
export const createDailyExpense = createAsyncThunk(
  "dailyExpense/createDailyExpense",
  async (dailyExpense, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const createdDailyExpense = await dailyExpenseService.createDailyExpense(
        dailyExpense,
        token
      );
      return createdDailyExpense;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//Get All DailyExpenses
export const getAllDailyExpenses = createAsyncThunk(
  "dailyExpense/getAllDailyExpenses",
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const dailyExpenses = await dailyExpenseService.getAllDailyExpenses(
        token
      );
      return dailyExpenses;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Get DailyExpense By Id
export const getDailyExpenseById = createAsyncThunk(
  "dailyExpense/getDailyExpenseById",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const dailyExpense = await dailyExpenseService.getDailyExpenseById(
        id,
        token
      );
      return dailyExpense;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Update DailyExpense
export const updateDailyExpense = createAsyncThunk(
  "dailyExpense/updateDailyExpense",
  async ({ id, dailyExpense }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const updatedDailyExpense = await dailyExpenseService.updateDailyExpense(
        id,
        dailyExpense,
        token
      );
      return updatedDailyExpense;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  } 
);

// Delete DailyExpense
export const deleteDailyExpense = createAsyncThunk(
  "dailyExpense/deleteDailyExpense",
  async (id, thunkAPI) => {
    try {
      const token = thunkAPI.getState().user.userInfo.token;
      const deletedDailyExpense = await dailyExpenseService.deleteDailyExpense(
        id,
        token
      );
      return deletedDailyExpense;
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const dailyExpenseSlice = createSlice({
  name: "dailyExpense",
  initialState,
  reducers: {
    resetExpenseCreate: (state) => {
      state.isCreateLoading = false;
      state.isCreateError = false;
      state.isCreateSuccess = false;
      state.createErrorMessage = "";
    },
    resetExpenseList: (state) => {
      state.dailyExpenses = [];
      state.isListLoading = false;
      state.isListError = false;
      state.isListSuccess = false;
      state.listErrorMessage = "";
    },
    resetExpenseDetails: (state) => {
      state.dailyExpense = null;
      state.isDetailsLoading = false;
      state.isDetailsError = false;
      state.isDetailsSuccess = false;
      state.detailsErrorMessage = "";
    },
    resetExpenseUpdate: (state) => {
      state.isUpdateLoading = false;
      state.isUpdateError = false;
      state.isUpdateSuccess = false;
      state.updateErrorMessage = "";
    },
    resetExpenseDelete: (state) => {
      state.isDeleteLoading = false;
      state.isDeleteError = false;
      state.isDeleteSuccess = false;
      state.deleteErrorMessage = "";
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createDailyExpense.pending, (state) => {
        state.isCreateLoading = true;
        state.isCreateError = false;
        state.isCreateSuccess = false;
        state.createErrorMessage = "";
      })
      .addCase(createDailyExpense.fulfilled, (state, action) => {
        state.isCreateLoading = false;
        state.isCreateSuccess = true;
        state.isCreateError = false;
        state.createErrorMessage = "";

        state.dailyExpenses.push(action.payload);
        state.dailyExpense = action.payload;
      })
      .addCase(createDailyExpense.rejected, (state, action) => {
        state.isCreateLoading = false;
        state.isCreateError = true;
        state.isCreateSuccess = false;
        state.createErrorMessage = action.payload;
      })
      .addCase(getAllDailyExpenses.pending, (state) => {
        state.isListLoading = true;
        state.isListError = false;
        state.isListSuccess = false;
        state.listErrorMessage = "";
      })
      .addCase(getAllDailyExpenses.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.isListError = false;
        state.isListSuccess = true;
        state.listErrorMessage = "";

        state.dailyExpenses = action.payload;
      })
      .addCase(getAllDailyExpenses.rejected, (state, action) => {
        state.isListLoading = false;
        state.isListError = true;
        state.isListSuccess = false;
        state.listErrorMessage = action.payload;
      })
      .addCase(getDailyExpenseById.pending, (state) => {
        state.isDetailsLoading = true;
        state.isDetailsError = false;
        state.isDetailsSuccess = false;
        state.detailsErrorMessage = "";
      })
      .addCase(getDailyExpenseById.fulfilled, (state, action) => {
        state.isDetailsLoading = false;
        state.isDetailsError = false;
        state.isDetailsSuccess = true;
        state.detailsErrorMessage = "";

        state.dailyExpense = action.payload;
      })
      .addCase(getDailyExpenseById.rejected, (state, action) => {
        state.isDetailsLoading = false;
        state.isDetailsError = true;
        state.isDetailsSuccess = false;
        state.detailsErrorMessage = action.payload;
      })
      .addCase(updateDailyExpense.pending, (state) => {
        state.isUpdateLoading = true;
        state.isUpdateError = false;
        state.isUpdateSuccess = false;
        state.updateErrorMessage = "";
      })
      .addCase(updateDailyExpense.fulfilled, (state, action) => {
        state.isUpdateLoading = false;
        state.isUpdateError = false;
        state.isUpdateSuccess = true;
        state.updateErrorMessage = "";

        state.dailyExpenses = state.dailyExpenses.map((dailyExpense) => {
          if (dailyExpense._id === action.payload._id) {
            return action.payload;
          }
          return dailyExpense;
        });
        state.dailyExpense = action.payload;
      })
      .addCase(updateDailyExpense.rejected, (state, action) => {
        state.isUpdateLoading = false;
        state.isUpdateError = true;
        state.isUpdateSuccess = false;
        state.updateErrorMessage = action.payload;
      })
      .addCase(deleteDailyExpense.pending, (state) => {
        state.isDeleteLoading = true;
        state.isDeleteError = false;
        state.isDeleteSuccess = false;
        state.deleteErrorMessage = "";
      })
      .addCase(deleteDailyExpense.fulfilled, (state, action) => {
        state.isDeleteLoading = false;
        state.isDeleteError = false;
        state.isDeleteSuccess = true;
        state.deleteErrorMessage = "";

        state.dailyExpenses = state.dailyExpenses.filter(
          (dailyExpense) => dailyExpense._id !== action.payload
        );
        state.dailyExpense = null;
      })
      .addCase(deleteDailyExpense.rejected, (state, action) => {
        state.isDeleteLoading = false;
        state.isDeleteError = true;
        state.isDeleteSuccess = false;
        state.deleteErrorMessage = action.payload;
      });
  },
});

export const {
  resetExpenseCreate,
  resetExpenseList,
  resetExpenseDetails,
  resetExpenseUpdate,
  resetExpenseDelete,
} = dailyExpenseSlice.actions;

export default dailyExpenseSlice.reducer;
